import {
  ExperienceRetriever,
  ExperienceRetrieverError
} from '../experience-retriever/experience-retriever';
import { CreativeTagEvent } from '../enums';
import { ExperiencePlayer } from '../components/experience-player';
import { EventEmitter } from '../types';
import { Experience, Tracking, TrackingClick } from '../types/experience';
import { ImageExperienceParser } from '../experience-parser/image-experience-parser';
import { CreativeEventEmitter } from '../event-emitter/creative-event-emitter';
import { CreativeTrackingService } from '../services/creative-tracking/creative-tracking-service';
import { AdContainer } from '../ad-container/ad-container';
import {
  ClickUrls,
  ClickUrlsRetriever
} from '../click-urls-retriever/click-urls-retriever';
import {
  ContextAugmentedWindow,
  ParametersExtractor
} from '../parameters-extractor/parameters-extractor';
import { EntryPoint, Player } from '../types/player';
import { ViewabilityObserverFactory } from '../services/viewability-observer/viewability-observer-factory';

declare const context: AdContext;
export class ExperienceEntryPointError extends Error {
  constructor(
    message: 'MissingImageUrl' | 'UnsupportedTemplateId',
    public details?: any
  ) {
    super(message);
    this.name = 'ExperienceEntryPointError';
  }
}

export class ExperienceEntryPoint implements EntryPoint {
  experiencePlayer: Player;
  protected experience: Experience | null = null;
  protected experienceParser: ImageExperienceParser | null = null;
  clickUrls: ClickUrls | null = null;
  isSmartCommerce: boolean = false;
  calendarDestinationUrls:
    | Tracking['calendarDestinationUrls']
    | undefined = undefined;
  calendarClicks: TrackingClick[] | undefined = undefined;
  deviceOS: string | undefined = undefined;

  protected creativeEventEmitter: EventEmitter<
    CreativeTagEvent
  > = new CreativeEventEmitter();

  constructor(
    protected experienceRetriever: ExperienceRetriever,
    protected creativeTrackingService: CreativeTrackingService,
    protected parametersExtractor: ParametersExtractor,
    protected adContainer: AdContainer,
    protected viewabilityObserverFactory: ViewabilityObserverFactory
  ) {
    this.experiencePlayer = new ExperiencePlayer(
      {
        activateTrackHandler: false
      },
      this.viewabilityObserverFactory
    );

    this.attachTrackingHandlers();
  }

  protected attachTrackingHandlers(): void {
    [
      CreativeTagEvent.CreativeLoadingFailed,
      CreativeTagEvent.CreativeAssetLoad,
      CreativeTagEvent.CreativeLoaded,
      CreativeTagEvent.CreativeDisplay,
      CreativeTagEvent.CreativeView,
      CreativeTagEvent.CreativeTimespent,
      CreativeTagEvent.CreativeShow,
      CreativeTagEvent.CreativeClicked
    ].forEach(event => {
      this.experiencePlayer.addEventListener(event, evt => {
        this.creativeEventEmitter.emit(event, evt);
      });
    });
  }

  getExperience(): Experience | null {
    return this.experience;
  }

  addEventListener(event: CreativeTagEvent, callback: (evt?: Event) => void) {
    this.creativeEventEmitter.addEventListener(event, callback);
  }

  emit(event: CreativeTagEvent, evt?: Event) {
    this.creativeEventEmitter.emit(event, evt);
  }

  async start(aWindow: Window) {
    try {
      const experienceFetchingPromise = this.experienceRetriever.fetchExperience(
        aWindow
      );
      this.emit(CreativeTagEvent.CreativeRequested);
      this.experience = await experienceFetchingPromise;
      this.startExperience(this.experience, aWindow);
    } catch (error) {
      this.onStartError(error as any);
    }
  }

  protected startExperience(experience: Experience, aWindow: Window) {
    this.experienceParser = this.getExperienceParser(experience);
    this.isSmartCommerce = this.experienceParser.isSmartCommerce;
    this.calendarDestinationUrls = this.experienceParser.getCalendarDestinationUrls();
    this.calendarClicks = this.experienceParser.getCalendarClicks();
    this.deviceOS = this.parametersExtractor.getContextParameters<string>(
      'deviceOs',
      aWindow
    );

    this.clickUrls = this.getClickUrls(
      this.experienceParser,
      this.parametersExtractor,
      aWindow as ContextAugmentedWindow
    );

    const imgUrl = this.experienceParser.getImageUrl();
    const backgroundColor = this.experienceParser.getBackgroundColor();
    const clickArea = this.experienceParser.getClickArea();
    const blurEffect = this.experienceParser.getBlurEffect();

    if (!imgUrl) {
      throw new ExperienceEntryPointError('MissingImageUrl');
    }

    this.experiencePlayer.startExperience({
      adContext: context,
      container: aWindow.document.getElementsByTagName('body')[0],
      window: aWindow,
      creativeProps: {
        creativeUrl: imgUrl,
        clickArea,
        backgroundColor,
        blurEffect,
        ratio: this.experienceParser.getRatio(),
        containerPolicy: this.experienceParser.getContainerPolicy()
      }
    });
  }

  protected getExperienceParser(experience: Experience) {
    if (experience?.metadata.templateId !== 'image') {
      throw new ExperienceEntryPointError('UnsupportedTemplateId', {
        templateId: experience?.metadata.templateId
      });
    }

    return new ImageExperienceParser(experience);
  }

  protected getClickUrls(
    imgExperienceParser: ImageExperienceParser,
    parametersExtractor: ParametersExtractor,
    aWindow: ContextAugmentedWindow
  ): ClickUrls {
    return new ClickUrlsRetriever(
      imgExperienceParser,
      parametersExtractor
    ).getClickUrls(aWindow);
  }

  protected onStartError(error: Error) {
    if (error instanceof ExperienceEntryPointError) {
      console.error(
        'Experience Entry Point Error:',
        error.message,
        error.details
      );
    } else if (error instanceof ExperienceRetrieverError) {
      console.error(
        'Experience Retriever Error:',
        error.message,
        error.details
      );
    } else {
      console.error('Unexpected Error:', error);
    }

    this.emit(CreativeTagEvent.CreativeLoadingFailed);
  }
}
